/**
 * Firebase client wiring for the Deployer UI.
 *
 * The single browser app reads **two Firestore databases in the same project**:
 * `(default)` (the release/deploy side, and operators/hosts) and `prepare` (the
 * preparation side). The Web SDK addresses a named database with
 * `getFirestore(app, 'prepare')`, so one app and one sign-in cover both — no
 * second project, no iframe. Storage points at the artifacts bucket for the log
 * viewer's archived-object fallback and the change-set files.
 */
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { DATABASE, databaseIdForRole, type HostRole, type HostStartRequest } from '@finvari/deployer';

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'finvari-release';
const STORAGE_BUCKET = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? 'finvari-release-artifacts';

export const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? 'demo-api-key',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? `${PROJECT_ID}.firebaseapp.com`,
    projectId: PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? 'demo-app-id',
    storageBucket: STORAGE_BUCKET,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// The two databases. `dbFor(role)` picks the one a given host writes.
export const dbDefault: Firestore = getFirestore(app);
export const dbPrepare: Firestore = getFirestore(app, DATABASE.prepare);

export const storage = getStorage(app, `gs://${STORAGE_BUCKET}`);
export const functions = getFunctions(app, 'us-central1');

/** The database a host role's documents live in. */
export function dbForRole(role: HostRole): Firestore {
    return databaseIdForRole(role) === DATABASE.prepare ? dbPrepare : dbDefault;
}

interface HostControlResult {
    host: HostRole;
    status: string;
    expectedBootSeconds?: number;
    /** The shape it was started on, when a machine type was asked for. */
    machineType?: string;
}

/**
 * Start a host VM via the privileged callable (operator claim enforced
 * server-side). A `machineType` must be one the host offers — the callable checks
 * it against `HOST_INSTANCES[role].machineTypeChoices` and that check, not IAM, is
 * what bounds the resize.
 */
export const startHost = httpsCallable<HostStartRequest, HostControlResult>(functions, 'startHost');
/** Stop a host VM via the privileged callable. */
export const stopHost = httpsCallable<{ host: HostRole }, HostControlResult>(functions, 'stopHost');

// Local development against the emulator suite (firebase.json ports).
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
    connectAuthEmulator(auth, 'http://127.0.0.1:19099', { disableWarnings: true });
    connectFirestoreEmulator(dbDefault, '127.0.0.1', 18080);
    connectFirestoreEmulator(dbPrepare, '127.0.0.1', 18080);
    connectStorageEmulator(storage, '127.0.0.1', 19199);
    connectFunctionsEmulator(functions, '127.0.0.1', 15001);
}
